import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Trash2, Plus } from "lucide-react";

/** Edit dialog for a project. Edits name/description/domain/status and the
 *  linked Emergent jobs array. Slug is immutable (shown read-only). */
export function EditProjectDialog({ open, onOpenChange, project, onSaved }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: "", description: "", domain: "", status: "active",
    emergent_jobs: [],
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!project) return;
    setForm({
      name: project.name || "",
      description: project.description || "",
      domain: project.domain || "",
      status: project.status || "active",
      emergent_jobs: (project.emergent_jobs || []).map((j) => ({
        job_id: j.job_id || "",
        label: j.label || "",
        preview_url: j.preview_url || "",
      })),
    });
  }, [project]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const addJob = () => setForm((f) => ({
    ...f, emergent_jobs: [...f.emergent_jobs, { job_id: "", label: "", preview_url: "" }],
  }));
  const removeJob = (i) => setForm((f) => ({
    ...f, emergent_jobs: f.emergent_jobs.filter((_, idx) => idx !== i),
  }));
  const setJob = (i, k, v) => setForm((f) => ({
    ...f,
    emergent_jobs: f.emergent_jobs.map((j, idx) => idx === i ? { ...j, [k]: v } : j),
  }));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    setBusy(true);
    try {
      const cleaned_jobs = form.emergent_jobs
        .filter((j) => j.job_id.trim())
        .map((j) => ({
          job_id: j.job_id.trim(),
          label: j.label.trim() || "job",
          preview_url: j.preview_url.trim() || null,
        }));
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        domain: form.domain.trim(),
        status: form.status,
        emergent_jobs: cleaned_jobs,
      };
      await api.patch(`/projects/${project.slug}`, payload);
      toast.success("Project saved.");
      onOpenChange(false);
      qc.invalidateQueries({ queryKey: ["project", project.slug] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!project) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent data-testid="edit-project-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Edit project</DialogTitle>
          <DialogDescription>
            Slug <span className="font-mono text-foreground">{project.slug}</span> is immutable.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="ep-name">Name</Label>
            <Input id="ep-name" data-testid="ep-name"
              value={form.name} onChange={(e) => set("name")(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="ep-desc">Description</Label>
            <Textarea id="ep-desc" data-testid="ep-description" rows={2}
              value={form.description} onChange={(e) => set("description")(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ep-domain">Domain</Label>
              <Input id="ep-domain" data-testid="ep-domain"
                value={form.domain} onChange={(e) => set("domain")(e.target.value)} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="ep-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="pt-2">
            <div className="flex items-center justify-between mb-2">
              <Label>Linked Emergent jobs</Label>
              <Button type="button" variant="outline" size="sm"
                onClick={addJob} data-testid="ep-add-job">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add job
              </Button>
            </div>
            {form.emergent_jobs.length === 0 ? (
              <p className="text-xs text-muted-foreground font-mono">
                No linked jobs. Some projects don't have a build pipeline.
              </p>
            ) : (
              <ul className="space-y-2" data-testid="ep-jobs-list">
                {form.emergent_jobs.map((j, i) => (
                  <li key={i}
                      className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center"
                      data-testid={`ep-job-row-${i}`}>
                    <Input placeholder="Job ID" value={j.job_id}
                      data-testid={`ep-job-id-${i}`}
                      onChange={(e) => setJob(i, "job_id", e.target.value)} />
                    <Input placeholder="Label (e.g. primary)" value={j.label}
                      data-testid={`ep-job-label-${i}`}
                      onChange={(e) => setJob(i, "label", e.target.value)} />
                    <Input placeholder="Preview URL (optional)" value={j.preview_url}
                      data-testid={`ep-job-url-${i}`}
                      onChange={(e) => setJob(i, "preview_url", e.target.value)} />
                    <Button type="button" variant="ghost" size="icon"
                      onClick={() => removeJob(i)}
                      data-testid={`ep-remove-job-${i}`}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" disabled={busy}
            onClick={() => onOpenChange(false)} data-testid="ep-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="ep-submit">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/** Confirm archive OR permanent-delete. For delete, requires exact slug match. */
export function ProjectActionDialog({ open, onOpenChange, project, action, canDelete, deleteReason, onDone }) {
  const qc = useQueryClient();
  const [slugInput, setSlugInput] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setSlugInput(""); }, [open, action]);

  if (!project) return null;

  const meta = action === "archive"
    ? { title: "Archive project",
        desc: `This hides "${project.name}" from active lists. Tasks and costs are kept. You can restore anytime.`,
        confirmLabel: "Archive", confirmCls: "bg-amber-600 hover:bg-amber-700 text-white",
        endpoint: `archive`, toast: "Project archived." }
    : action === "restore"
    ? { title: "Restore project",
        desc: `Restores "${project.name}" to the active roster.`,
        confirmLabel: "Restore", confirmCls: "bg-emerald-600 hover:bg-emerald-700 text-white",
        endpoint: `restore`, toast: "Project restored." }
    : { title: "Permanently delete project",
        desc: `This cannot be undone. Type the slug "${project.slug}" to confirm.`,
        confirmLabel: "Delete forever", confirmCls: "bg-rose-600 hover:bg-rose-700 text-white",
        endpoint: null, toast: "Project deleted." };

  const canConfirm = action === "delete"
    ? (slugInput === project.slug && canDelete && !busy)
    : !busy;

  const submit = async () => {
    setBusy(true);
    try {
      if (action === "delete") {
        await api.delete(`/projects/${project.slug}`, {
          params: { confirm_slug: slugInput },
        });
      } else {
        await api.post(`/projects/${project.slug}/${meta.endpoint}`);
      }
      toast.success(meta.toast);
      onOpenChange(false);
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["project", project.slug] });
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent data-testid={`project-${action}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display">{meta.title}</DialogTitle>
          <DialogDescription>{meta.desc}</DialogDescription>
        </DialogHeader>
        {action === "delete" && !canDelete && (
          <p className="text-sm text-rose-500 font-mono" data-testid="delete-blocked-reason">
            {deleteReason || "Cannot delete — archive instead."}
          </p>
        )}
        {action === "delete" && canDelete && (
          <div>
            <Label htmlFor="pd-slug">Type the slug to confirm</Label>
            <Input id="pd-slug" value={slugInput}
              data-testid="pd-slug-input"
              onChange={(e) => setSlugInput(e.target.value)}
              placeholder={project.slug} autoComplete="off" />
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" disabled={busy}
            onClick={() => onOpenChange(false)} data-testid={`pa-cancel-${action}`}>Cancel</Button>
          <Button onClick={submit} disabled={!canConfirm}
            className={meta.confirmCls}
            data-testid={`pa-confirm-${action}`}>
            {busy ? "…" : meta.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
